import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { translateObject } from '@jsverse/transloco';

import { EnumErrorStates } from '@inmoment/the-kitchen';

import { ConfigurationRoute } from 'app/providers/semantria-api/configuration-route.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-route-details-form',
  templateUrl: './configuration-route-details-form.component.html',
  styleUrls: ['./configuration-route-details-form.component.scss'],
  standalone: false,
})
export class ConfigurationRouteDetailsFormComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() name: string;
  @Input() origRoute: ConfigurationRoute;
  @Input() notes: string;
  @Input() nameValidator;
  @Input() canEditRoute;
  isLoadingName = false;
  isLoadingNotes = false;

  @Output() nameChange = new EventEmitter<string>();
  @Output() notesChange = new EventEmitter<string>();
  @Output() validityChange = new EventEmitter<boolean>();

  form: UntypedFormGroup;
  configNameStatus = { state: EnumErrorStates.SUCCESS, text: '' };

  constructor(private formBuilder: UntypedFormBuilder) {
    this.form = this.formBuilder.group({
      configName: ['', Validators.compose([Validators.required, this.nameValidator])],
      configNotes: [''],
    });
  }

  ngOnInit(): void {
    this.form.controls.configName.valueChanges.subscribe((val) => this.nameChange.emit(val));
    this.form.controls.configNotes.valueChanges.subscribe((val) => this.notesChange.emit(val));
    this.form.statusChanges.pipe().subscribe((status) => {
      this.configNameStatus.state = EnumErrorStates.SUCCESS;
      this.configNameStatus.text = '';

      if (status === 'INVALID') {
        this.configNameStatus.state = EnumErrorStates.ERROR;

        const error = this.form.controls.configName.errors;
        const errorTranslations = translateObject(
          'configuration-routes.view.settings-tab.name-errors',
          {
            'min-length': { charLength: error.minLength?.minLength },
            'max-length': { charLength: error.maxLength?.maxLength },
          }
        );

        switch (Object.keys(error)[0]) {
          case 'required':
            this.configNameStatus.text = errorTranslations['required'];
            break;
          case 'minLength':
            this.configNameStatus.text = errorTranslations['min-length'];
            break;
          case 'maxLength':
            this.configNameStatus.text = errorTranslations['max-length'];
            break;
          case 'nameAlreadyExists':
            this.configNameStatus.text = errorTranslations['exists'];
            break;
        }
      }
      this.validityChange.emit(status !== 'INVALID');
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.nameValidator) {
      this.form.controls.configName.setValidators(
        Validators.compose([Validators.required, this.nameValidator])
      );
      this.form.updateValueAndValidity();
    }
    if (changes.name && changes.name.currentValue !== this.form.controls.configName.value) {
      this.form.controls.configName.patchValue(changes.name.currentValue);
    }
    if (changes.notes && changes.notes.currentValue !== this.form.controls.configNotes.value) {
      this.form.controls.configNotes.patchValue(changes.notes.currentValue);
      //XI hack due to inputs not reflecting reactive form control values
      this.isLoadingNotes = true;
      setTimeout(() => (this.isLoadingNotes = false));
    }
  }
}
